import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = process.env.REACT_APP_BACKEND_URL;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};

const categories = ["Business", "Insurance", "Finance", "Leadership", "Wellness", "Strategy"];
const emptyForm = { title: "", slug: "", excerpt: "", content: "", featured_image: "", author: "Ekeyah Wallace", category: "Business", published: false };

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [view, setView] = useState("list");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/blog`); setPosts(data); }
    catch (err) { if (err?.response?.status !== 404) console.error("Load blog:", err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ ...emptyForm }); setEditing(null); setView("list"); };

  const savePost = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      if (editing) { await axios.put(`${API}/api/admin/blog/${editing}`, form); toast.success("Post updated!"); }
      else { await axios.post(`${API}/api/admin/blog`, form); toast.success("Post created!"); }
      resetForm(); load();
    } catch { toast.error("Failed to save post"); }
  };

  const editPost = (post) => {
    setEditing(post.id);
    setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt, content: post.content, featured_image: post.featured_image, author: post.author, category: post.category, published: post.published });
    setView("editor");
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await axios.delete(`${API}/api/admin/blog/${id}`); load(); toast.success("Post deleted"); } catch { toast.error("Failed"); }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/api/admin/blog/${post.id}`, { ...post, published: !post.published });
      load(); toast.success(post.published ? "Unpublished" : "Published!");
    } catch { toast.error("Failed"); }
  };

  const filtered = posts.filter(p => {
    if (filter === "published" && !p.published) return false;
    if (filter === "draft" && p.published) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (view === "editor") {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <button onClick={resetForm} style={{ display: "flex", alignItems: "center", gap: ".4rem", background: "none", border: "none", color: "#6B7280", cursor: "pointer", fontSize: ".85rem" }}>
            <i className="fas fa-arrow-left" /> Back to Posts
          </button>
          <div style={{ display: "flex", gap: ".5rem" }}>
            <button onClick={() => { setForm(f => ({ ...f, published: false })); savePost(); }} style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", color: "#374151", cursor: "pointer", fontSize: ".82rem", fontWeight: 600 }}>
              Save Draft
            </button>
            <button onClick={() => { setForm(f => ({ ...f, published: true })); savePost(); }} style={{ padding: ".5rem 1rem", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600 }}>
              {editing ? "Update & Publish" : "Publish"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1.5rem" }}>
          {/* Main Editor */}
          <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Post Title" data-testid="blog-title-input"
              style={{ width: "100%", padding: ".7rem 0", border: "none", fontSize: "1.5rem", fontWeight: 700, color: "#1F2937", outline: "none", borderBottom: "1px solid #E5E7EB", marginBottom: "1rem" }} />
            <input value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} placeholder="Write a short excerpt..." data-testid="blog-excerpt-input"
              style={{ width: "100%", padding: ".5rem 0", border: "none", fontSize: ".9rem", color: "#6B7280", outline: "none", marginBottom: "1rem" }} />
            <ReactQuill theme="snow" value={form.content} onChange={v => setForm(p => ({ ...p, content: v }))} modules={quillModules} style={{ borderRadius: 8 }} data-testid="blog-content-editor" />
          </div>

          {/* Sidebar Settings */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Status */}
            <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Status</h4>
              <label style={{ display: "flex", alignItems: "center", gap: ".5rem", cursor: "pointer" }}>
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} data-testid="blog-published-toggle"
                  style={{ accentColor: "#EC4899" }} />
                <span style={{ fontSize: ".85rem", fontWeight: 500 }}>{form.published ? "Published" : "Draft"}</span>
              </label>
            </div>

            {/* Category */}
            <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Category</h4>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} data-testid="blog-category-select"
                style={{ width: "100%", padding: ".5rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Author */}
            <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Author</h4>
              <input value={form.author} onChange={e => setForm(p => ({ ...p, author: e.target.value }))} style={{ width: "100%", padding: ".5rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
            </div>

            {/* Featured Image */}
            <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>Featured Image</h4>
              <input value={form.featured_image} onChange={e => setForm(p => ({ ...p, featured_image: e.target.value }))} placeholder="https://..." style={{ width: "100%", padding: ".5rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".82rem", marginBottom: ".5rem" }} />
              {form.featured_image && <img src={form.featured_image} alt="Preview" style={{ width: "100%", borderRadius: 8, maxHeight: 140, objectFit: "cover" }} />}
            </div>

            {/* SEO */}
            <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              <h4 style={{ fontSize: ".8rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem", textTransform: "uppercase", letterSpacing: ".05em" }}>SEO</h4>
              <input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="auto-generated-slug" data-testid="blog-slug-input"
                style={{ width: "100%", padding: ".5rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".82rem", color: "#6B7280" }} />
              <p style={{ fontSize: ".7rem", color: "#9CA3AF", margin: ".25rem 0 0" }}>Leave empty to auto-generate from title</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
          {["all", "published", "draft"].map(f => {
            const count = f === "all" ? posts.length : f === "published" ? posts.filter(p => p.published).length : posts.filter(p => !p.published).length;
            return (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: ".4rem .8rem", borderRadius: 6, border: "none", fontSize: ".8rem", fontWeight: 500, cursor: "pointer",
                background: filter === f ? "#1F2937" : "#F3F4F6", color: filter === f ? "#fff" : "#6B7280",
              }}>{f.charAt(0).toUpperCase() + f.slice(1)} ({count})</button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <i className="fas fa-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: ".75rem" }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search posts..." style={{ padding: ".4rem .7rem .4rem 2rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".82rem", width: 200 }} />
          </div>
          <button onClick={() => { resetForm(); setView("editor"); }} style={{
            display: "flex", alignItems: "center", gap: ".4rem", padding: ".5rem 1rem", borderRadius: 8, border: "none",
            background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600,
          }}>
            <i className="fas fa-plus" /> New Post
          </button>
        </div>
      </div>

      {/* Posts Table */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.06)", overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#9CA3AF" }}>
            <i className="fas fa-pen-fancy" style={{ fontSize: "2rem", marginBottom: ".5rem", display: "block" }} />
            <p>No posts found</p>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E5E7EB", background: "#F9FAFB" }}>
                <th style={{ textAlign: "left", padding: ".7rem 1rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: ".05em" }}>Title</th>
                <th style={{ textAlign: "left", padding: ".7rem .5rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Status</th>
                <th style={{ textAlign: "left", padding: ".7rem .5rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Category</th>
                <th style={{ textAlign: "left", padding: ".7rem .5rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Author</th>
                <th style={{ textAlign: "left", padding: ".7rem .5rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Date</th>
                <th style={{ textAlign: "right", padding: ".7rem 1rem", fontSize: ".75rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(post => (
                <tr key={post.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                  <td style={{ padding: ".7rem 1rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                      {post.featured_image ? (
                        <img src={post.featured_image} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: 6, background: "linear-gradient(135deg, #EC4899, #F97316)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: ".7rem", flexShrink: 0 }}>
                          <i className="fas fa-pen" />
                        </div>
                      )}
                      <span style={{ fontWeight: 600, color: "#1F2937", fontSize: ".85rem" }}>{post.title}</span>
                    </div>
                  </td>
                  <td style={{ padding: ".7rem .5rem" }}>
                    <span style={{ padding: ".2rem .6rem", borderRadius: 50, fontSize: ".7rem", fontWeight: 600, background: post.published ? "rgba(16,185,129,.1)" : "rgba(107,114,128,.1)", color: post.published ? "#059669" : "#6B7280" }}>
                      {post.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td style={{ padding: ".7rem .5rem", color: "#6B7280", fontSize: ".82rem" }}>{post.category}</td>
                  <td style={{ padding: ".7rem .5rem", color: "#6B7280", fontSize: ".82rem" }}>{post.author}</td>
                  <td style={{ padding: ".7rem .5rem", color: "#9CA3AF", fontSize: ".82rem" }}>{new Date(post.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: ".7rem 1rem", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: ".3rem", justifyContent: "flex-end" }}>
                      <button onClick={() => editPost(post)} title="Edit" style={{ width: 30, height: 30, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", color: "#6B7280", fontSize: ".75rem" }}>
                        <i className="fas fa-edit" />
                      </button>
                      <button onClick={() => togglePublish(post)} title={post.published ? "Unpublish" : "Publish"} style={{ width: 30, height: 30, borderRadius: 6, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", color: post.published ? "#F59E0B" : "#10B981", fontSize: ".75rem" }}>
                        <i className={`fas ${post.published ? "fa-eye-slash" : "fa-eye"}`} />
                      </button>
                      <button onClick={() => deletePost(post.id)} title="Delete" style={{ width: 30, height: 30, borderRadius: 6, border: "1px solid #FEE2E2", background: "#FEF2F2", cursor: "pointer", color: "#EF4444", fontSize: ".75rem" }}>
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
